import {
  TranscriptElement,
  TextElement,
  PunctElement,
} from './TranscriptElement';

import { IParagraph, ITextElement } from './interfaces';

import { makeAutoObservable } from 'mobx';

/**
 * Paragraph represents a logical grouping of content within a segment
 * It contains a list of text and punctuation elements
 */
export class Paragraph implements IParagraph {
  /** Unique identifier for the paragraph */
  id: string;
  
  /** Start time of the paragraph in seconds */
  startTime: number;
  
  /** End time of the paragraph in seconds */
  endTime: number;
  
  /** List of text and punctuation elements in the paragraph */
  elements: TranscriptElement[];

  /** ID of the segment that contains this paragraph */
  segmentId: string;
  
  /** Counter for the paragraph */
  counter: number;

  /** Maps elementId to index in the elements array for quick lookup */
  private elementMap: Map<string, number>;
  
  /**
   * Creates a new Paragraph
   * 
   * @param id Unique identifier for the paragraph
   * @param startTime Start time of the paragraph in seconds
   * @param endTime End time of the paragraph in seconds
   * @param elements List of elements in the paragraph
   * @param segmentId ID of the segment that contains this paragraph
   */
  constructor(
    id: string,
    startTime: number,
    endTime: number,
    elements: (TextElement | PunctElement)[] = [],
    segmentId: string
  ) {


    this.id = id;
    this.startTime = startTime;
    this.endTime = endTime;
    this.elements = elements;
    this.segmentId = segmentId;
    this.counter = 0;
    // Build the element lookup map
    this.elementMap = new Map();
    this.rebuildElementMap();

    // Deep observability for reactive UI updates.
    makeAutoObservable(this);
  }
  
  /**
   * Rebuilds the element lookup map
   * Call this after any operations that modify the elements array
   */
  rebuildElementMap(): void {
    this.elementMap.clear();
    this.elements.forEach((element, index) => {
      this.elementMap.set(element.id, index);
    });
    this.counter++;
  }
  
  /**
   * Updates internal data making sure all children are referring to proper parent
   * at segment/paragraph/element level
   * 
   * @param segmentId ID of the segment that contains this paragraph
   * @param speakerLabel Optional speaker label
   */
  updateInternalData(segmentId: string, speakerLabel?: string): void {
    this.segmentId = segmentId;
    
    // Update element parent references
    this.elements.forEach(element => {
      element.updateInternalData(segmentId, this.id, speakerLabel);
    });
    this.counter++;
  }

  /**
   * Gets a child entity by ID (implements ParentEntity)
   */
  getChild(elementId: string): TranscriptElement | undefined {
    return this.getElement(elementId);
  }
  
  /**
   * Adds a child entity (implements ParentEntity)
   */
  addChild(element: TranscriptElement): void {
    this.addElement(element);
  }
  
  /**
   * Removes a child entity (implements ParentEntity)
   */
  removeChild(elementId: string): boolean {
    return this.removeElement(elementId);
  }
  
  /**
   * Updates a child entity (implements ParentEntity)
   */
  updateChild(elementId: string, updatedElement: TranscriptElement): boolean {
    return this.updateElement(elementId, updatedElement);
  }

  /**
   * Gets an element by ID
   * 
   * @param elementId ID of the element to get
   * @returns The element, or undefined if not found
   */
  getElement(elementId: string): (TextElement | PunctElement) | undefined {
    const index = this.elementMap.get(elementId);
    return index !== undefined ? this.elements[index] : undefined;
  }
  
  /**
   * Adds an element to the paragraph
   * Updates paragraph timing if necessary
   * 
   * @param element Element to add
   */
  addElement(element: TextElement | PunctElement): void {
    // Update parent references
    element.paragraphId = this.id;
    element.segmentId = this.segmentId;
    
    // Add to elements array
    this.elements.push(element);
    this.elementMap.set(element.id, this.elements.length - 1);
    
    // Update paragraph timing if necessary
    this.updateTimingFromElements();
    this.counter++;
  }
  
  /**
   * Removes an element from the paragraph
   * 
   * @param elementId ID of the element to remove
   * @returns true if the element was removed, false if not found
   */
  removeElement(elementId: string): boolean {
    const index = this.elementMap.get(elementId);
    if (index === undefined) {
      return false;
    }
    
    this.elements.splice(index, 1);
    this.rebuildElementMap();
    
    // Update paragraph timing
    this.updateTimingFromElements();
    this.counter++;
    return true;
  }
  
  /**
   * Updates an element in the paragraph
   * 
   * @param elementId ID of the element to update
   * @param updatedElement Updated element data
   * @returns true if the element was updated, false if not found
   */
  updateElement(elementId: string, updatedElement: TextElement | PunctElement): boolean {
    const index = this.elementMap.get(elementId);
    if (index === undefined) {
      return false;
    }
    
    // Ensure parent references are maintained
    updatedElement.paragraphId = this.id;
    updatedElement.segmentId = this.segmentId;
    
    // Update element
    this.elements[index] = updatedElement;
    this.counter++;
    // Update paragraph timing
    this.updateTimingFromElements();
    
    return true;
  }
  
  /**
   * Updates the paragraph's start and end times based on its elements
   */
  private updateTimingFromElements(): void {
    if (this.elements.length === 0) {
      return;
    }
    
    // Find the earliest start time and latest end time from text elements
    let earliestStart = Number.MAX_VALUE;
    let latestEnd = 0;
    
    // Get text elements with valid timing data
    const validElements = this.elements.filter(e => 
      e.type === 'text' && 
      typeof (e as TextElement).startTime === 'number' && 
      typeof (e as TextElement).endTime === 'number' &&
      !isNaN((e as TextElement).startTime as number) &&
      !isNaN((e as TextElement).endTime as number)
    ) as TextElement[];
    
    if (validElements.length === 0) {
      return;
    }
    
    for (const element of validElements) {
      if (element.startTime !== undefined && element.startTime < earliestStart) {
        earliestStart = element.startTime;
      }
      if (element.endTime !== undefined && element.endTime > latestEnd) {
        latestEnd = element.endTime;
      }
    }
    
    // Only update if we found valid times
    if (earliestStart !== Number.MAX_VALUE) {
      this.startTime = earliestStart;
    }
    
    if (latestEnd > 0) {
      this.endTime = latestEnd;
    }
    
    // Ensure start time is not greater than end time
    if (this.startTime > this.endTime) {
      // If timing is invalid, add a small buffer to make duration valid
      this.endTime = this.startTime + 0.001;
    }
    this.counter++;
  }
  
  /**
   * Gets the duration of the paragraph in seconds
   */
  get duration(): number {
    return this.endTime - this.startTime;
  }
  
  /**
   * Gets the textual content of the paragraph, combining all elements
   */
  getText(): string {
    return this.elements.map(element => element.value).join('');
  }
  
  /**
   * Gets only the text elements in the paragraph
   */
  getTextElements(): TextElement[] {
    return this.elements.filter(
      (element): element is TextElement => element.type === 'text'
    );
  }
  
  /**
   * Creates a clone of this paragraph with new IDs
   * @param newSegmentId Optional new segment ID 
   * @param preserveId Whether to preserve the original paragraph ID
   * @param preserveElementIds Whether to preserve the original element IDs
   * @returns A new paragraph with cloned elements
   */
  cloneWithNewIds(
    newSegmentId?: string, 
    preserveId: boolean = false,
    preserveElementIds: boolean = false
  ): Paragraph {
    // Generate new ID or preserve existing one
    const paragraphId = preserveId 
      ? this.id 
      : `para-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

    
    // Clone the paragraph with the appropriate ID
    const clonedParagraph = new Paragraph(
      paragraphId,
      this.startTime,
      this.endTime, 
      [], // Empty elements array at first
      newSegmentId || this.segmentId
    );
    
    // Clone all elements with appropriate IDs
    const clonedElements = this.elements.map(element => {
      const clonedElement = element.clone(undefined, preserveElementIds);
      clonedElement.paragraphId = paragraphId;
      if (newSegmentId) {
        clonedElement.segmentId = newSegmentId;
      }
      return clonedElement;
    });
    
    // Add cloned elements to the paragraph
    clonedParagraph.elements = clonedElements;
    clonedParagraph.rebuildElementMap();
    
    return clonedParagraph;
  }
  
  /**
   * Serializes the paragraph to JSON
   */
  toJSON(): Record<string, any> {
    return {
      id: this.id,
      startTime: this.startTime,
      endTime: this.endTime,
      segmentId: this.segmentId,
      elements: this.elements.map(element => element.toJSON())
    };
  }
  
  /**
   * Creates a Paragraph from a JSON object
   * 
   * @param json JSON object to deserialize
   * @returns A new Paragraph instance
   */
  static fromJSON(json: any): Paragraph {
    // Import element factory to create elements
    const { TranscriptElementFactory } = require('./TranscriptElementFactory');
    
    const paragraph = new Paragraph(
      json.id,
      json.startTime,
      json.endTime,
      [],
      json.segmentId
    );
    
    // Deserialize elements
    paragraph.elements = json.elements.map((elementJson: any) => 
      TranscriptElementFactory.fromJSON(elementJson)
    );
    
    // Rebuild the element map
    paragraph.rebuildElementMap();
    
    return paragraph;
  }

  /**
   * Increments the counter for the paragraph
   */
  incrementCounter(): void {
    this.counter++;
  }
}